// src/components/SetInitialPassword.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Login.css';
import { FaSpinner, FaLock, FaCheckCircle } from 'react-icons/fa';
import logoBLB from '../logo_blb.png';

/* ──────────────────────────────────────────────────────────────────────────
   CSS adicional (injetado uma vez)
   ────────────────────────────────────────────────────────────────────────── */
const EXTRA_CSS = `
  .sip-layout {
    display: flex;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #ffffff;
  }

  .sip-left {
    flex: 0 0 45%;
    min-width: 480px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 80px;
    background: #ffffff;
    z-index: 10;
    position: relative;
    box-shadow: 15px 0 40px rgba(0,0,0,0.04);
    transition:
      flex-basis 0.75s cubic-bezier(0.16, 1, 0.3, 1),
      min-width   0.75s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow  0.4s ease;
  }
  .sip-left.sip-full {
    flex: 0 0 100%;
    min-width: 100%;
    box-shadow: none;
  }

  .sip-right {
    flex: 1;
    position: relative;
    background-color: #0f172a;
    clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
    margin-left: -10%;
    z-index: 1;
    transition:
      opacity   0.45s ease,
      transform 0.55s cubic-bezier(0.4, 0, 1, 1);
  }
  .sip-right.sip-exit {
    opacity: 0;
    transform: translateX(60px);
    pointer-events: none;
  }

  .sip-form-wrap {
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
    animation: slideRight 0.6s cubic-bezier(0.16,1,0.3,1) forwards;
  }
  .sip-form-wrap.sip-form-exit {
    animation: sipFadeOut 0.3s ease forwards;
    pointer-events: none;
  }

  .sip-success-wrap {
    width: 100%;
    max-width: 480px;
    margin: 0 auto;
    text-align: center;
    animation: sipSuccessIn 0.65s cubic-bezier(0.16,1,0.3,1) forwards;
  }

  .sip-check-ring {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #00d2ff, #00e5bf);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 28px;
    animation: sipRingPulse 1.6s ease 0.3s infinite;
  }

  .sip-strength-bar {
    height: 3px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
    margin: 8px 0 4px;
  }
  .sip-strength-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.35s ease, background 0.35s ease;
  }

  .sip-rules {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 12px 0 20px;
  }
  .sip-rule {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #94a3b8;
    transition: color 0.2s ease;
  }
  .sip-rule.ok   { color: #00a88d; }
  .sip-rule.fail { color: #ef4444; }
  .sip-rule-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #cbd5e1;
    flex-shrink: 0;
    transition: background 0.2s ease;
  }
  .sip-rule.ok   .sip-rule-dot { background: #00e5bf; }
  .sip-rule.fail .sip-rule-dot { background: #ef4444; }

  .sip-countdown {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #64748b;
    font-size: 1rem;
    font-weight: 800;
    margin: 20px auto 0;
    border: 2px solid #e2e8f0;
  }

  @keyframes sipFadeOut {
    to { opacity: 0; transform: translateY(-10px); }
  }
  @keyframes sipSuccessIn {
    from { opacity: 0; transform: translateY(24px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }
  @keyframes sipRingPulse {
    0%   { box-shadow: 0 0 0 0    rgba(0,229,191,0.35); }
    60%  { box-shadow: 0 0 0 18px rgba(0,229,191,0);    }
    100% { box-shadow: 0 0 0 0    rgba(0,229,191,0);    }
  }

  @media (max-width: 960px) {
    .sip-right { display: none !important; }
    .sip-left  { flex: 1 !important; min-width: auto !important; padding: 40px 5%; }
    .sip-left.sip-full { padding: 40px 5%; }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('sip-styles')) {
  const s = document.createElement('style');
  s.id = 'sip-styles';
  s.textContent = EXTRA_CSS;
  document.head.appendChild(s);
}

/* ──────────────────────────────────────────────────────────────────────────
   Ícone de olho
   ────────────────────────────────────────────────────────────────────────── */
const EyeIcon = ({ visible }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
        <line x1="2" x2="22" y1="2" y2="22" />
      </>
    )}
  </svg>
);

/* ──────────────────────────────────────────────────────────────────────────
   Regras de senha
   ────────────────────────────────────────────────────────────────────────── */
const RULES = [
  { key: 'len',   label: 'Mínimo 6 caracteres',  test: (v) => v.length >= 6 },
  { key: 'upper', label: 'Uma letra maiúscula',   test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'Uma letra minúscula',   test: (v) => /[a-z]/.test(v) },
  { key: 'num',   label: 'Um número',             test: (v) => /\d/.test(v) },
];

const strengthMeta = [
  { label: 'Muito fraca', color: '#ef4444', width: '20%'  },
  { label: 'Fraca',       color: '#f97316', width: '40%'  },
  { label: 'Razoável',    color: '#eab308', width: '65%'  },
  { label: 'Boa',         color: '#22c55e', width: '85%'  },
  { label: 'Forte',       color: '#00e5bf', width: '100%' },
];

/* ──────────────────────────────────────────────────────────────────────────
   Componente principal
   ────────────────────────────────────────────────────────────────────────── */
export default function SetInitialPassword() {
  const { userId: changePasswordTicket } = useParams();
  const navigate = useNavigate();

  const [passwords, setPasswords]     = useState({ newPassword: '', confirmPassword: '' });
  const [showNew, setShowNew]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');
  const [phase, setPhase]             = useState('form'); // 'form' | 'exiting' | 'success'
  const [countdown, setCountdown]     = useState(4);

  const ruleResults = RULES.map(r => ({ ...r, ok: r.test(passwords.newPassword) }));
  const strength    = ruleResults.filter(r => r.ok).length;
  const meta        = strengthMeta[Math.max(0, strength - 1)];

  /* Countdown após sucesso */
  useEffect(() => {
    if (phase !== 'success') return;
    if (countdown <= 0) { navigate('/login'); return; }
    const t = setTimeout(() => setCountdown(p => p - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown, navigate]);

  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setPasswords(prev => ({ ...prev, [id]: value }));
    setError('');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const newPass     = passwords.newPassword.trim();
    const confirmPass = passwords.confirmPassword.trim();

    if (newPass !== confirmPass) {
      setError('As senhas não coincidem. Verifique e tente novamente.');
      return;
    }
    if (newPass.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/set-initial-password', {
        changePasswordTicket,
        newPassword: newPass,
      });

      setSuccessMsg(data.message || 'Senha definida com sucesso!');
      setPhase('exiting');
      setTimeout(() => setPhase('success'), 380);

    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar a senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = phase === 'success';
  const isExiting = phase === 'exiting';

  return (
    <div className="sip-layout">

      {/* ── PAINEL ESQUERDO ── */}
      <div className={`sip-left ${isSuccess ? 'sip-full' : ''}`}>

        {/* Formulário */}
        {!isSuccess && (
          <div className={`sip-form-wrap ${isExiting ? 'sip-form-exit' : ''}`}>

            <div className="blb-branding">
              <div className="blb-logo-wrapper">
                <img src={logoBLB} alt="BLB System" className="login-logo-blb" />
              </div>
              <h2 className="login-title">Criar Nova Senha</h2>
              <p className="login-subtitle">Por segurança, defina uma senha pessoal para continuar.</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form-area">

              {/* Nova senha */}
              <div className="form-group">
                <label htmlFor="newPassword">NOVA SENHA</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNew ? 'text' : 'password'}
                    id="newPassword"
                    value={passwords.newPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowNew(p => !p)}
                    tabIndex={-1}
                    aria-label={showNew ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    <EyeIcon visible={showNew} />
                  </button>
                </div>

                {passwords.newPassword.length > 0 && (
                  <>
                    <div className="sip-strength-bar">
                      <div
                        className="sip-strength-fill"
                        style={{ width: meta.width, background: meta.color }}
                      />
                    </div>
                    <span style={{ fontSize: '0.75rem', color: meta.color, fontWeight: 700 }}>
                      {meta.label}
                    </span>
                  </>
                )}
              </div>

              {/* Checklist de regras */}
              {passwords.newPassword.length > 0 && (
                <div className="sip-rules">
                  {ruleResults.map(r => (
                    <span key={r.key} className={`sip-rule ${r.ok ? 'ok' : ''}`}>
                      <span className="sip-rule-dot" />
                      {r.label}
                    </span>
                  ))}
                </div>
              )}

              {/* Confirmar senha */}
              <div className="form-group">
                <label htmlFor="confirmPassword">CONFIRMAR SENHA</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="confirmPassword"
                    value={passwords.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirm(p => !p)}
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    <EyeIcon visible={showConfirm} />
                  </button>
                </div>

                {passwords.confirmPassword.length > 0 && (
                  <span style={{
                    fontSize: '0.75rem', fontWeight: 700,
                    marginTop: 5, display: 'block',
                    color: passwords.newPassword === passwords.confirmPassword ? '#00a88d' : '#ef4444',
                  }}>
                    {passwords.newPassword === passwords.confirmPassword
                      ? '✓ Senhas coincidem'
                      : '✗ Senhas não coincidem'}
                  </span>
                )}
              </div>

              {error && (
                <div className="alert-error-container">
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="login-button blb-btn"
                disabled={loading || strength < 1}
              >
                {loading
                  ? <FaSpinner className="spinner" />
                  : <><FaLock size={14} /> Salvar Nova Senha</>
                }
              </button>

            </form>
          </div>
        )}

        {/* Sucesso */}
        {isSuccess && (
          <div className="sip-success-wrap">
            <div className="sip-check-ring">
              <FaCheckCircle size={36} color="#0f172a" />
            </div>
            <h2 style={{
              fontSize: '1.9rem', fontWeight: 800,
              color: '#0f172a', margin: '0 0 12px', letterSpacing: '-0.5px',
            }}>
              Senha definida!
            </h2>
            <p style={{
              color: '#64748b', fontSize: '1rem',
              lineHeight: 1.6, maxWidth: 360, margin: '0 auto 8px',
            }}>
              {successMsg}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: 16 }}>
              Redirecionando para o login em alguns instantes…
            </p>
            <div className="sip-countdown">{countdown}</div>
            <button
              onClick={() => navigate('/login')}
              style={{
                marginTop: 28,
                background: 'none',
                border: '1.5px solid #00e5bf',
                borderRadius: 12,
                color: '#00a88d',
                fontWeight: 700,
                fontSize: '0.9rem',
                padding: '11px 28px',
                cursor: 'pointer',
              }}
            >
              Ir para o Login agora
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="blb-footer">
          <span>&copy; {new Date().getFullYear()} BLB System. Todos os direitos reservados.</span>
        </div>
      </div>

      {/* ── PAINEL DIREITO ── */}
      <div className={`sip-right ${isExiting || isSuccess ? 'sip-exit' : ''}`}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2070&auto=format&fit=crop)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(15,23,42,0.88) 0%, rgba(0,229,191,0.38) 100%)',
          zIndex: 2,
        }} />
        <div style={{
          position: 'absolute', bottom: 60, left: 60, right: 40,
          zIndex: 3, color: '#fff',
        }}>
          <p style={{
            fontSize: '1.5rem', fontWeight: 800,
            lineHeight: 1.3, margin: '0 0 10px', letterSpacing: '-0.3px',
          }}>
            Segurança em primeiro lugar.
          </p>
          <p style={{
            fontSize: '0.95rem', color: 'rgba(255,255,255,0.62)',
            fontWeight: 500, lineHeight: 1.5,
          }}>
            Sua nova senha protegerá todos os dados e operações da plataforma.
          </p>
        </div>
      </div>

    </div>
  );
}